<template>
  <div class="weather-widget">

    <div class="weather-city-selector" role="group" aria-label="Select city">
      <button
        v-for="city in presetCities"
        :key="city.key"
        type="button"
        :class="['weather-city-btn', { 'weather-city-btn--active': selectedCity === city.key }]"
        @click="selectPresetCity(city)"
      >{{ city.label }}</button>
      <button
        type="button"
        :class="['weather-city-btn', { 'weather-city-btn--active': selectedCity === 'custom' }]"
        @click="selectCustom"
      >Custom</button>
    </div>

    <div v-if="selectedCity === 'custom'" class="weather-custom-city">
      <input
        v-model="customCityName"
        type="text"
        class="form-control"
        placeholder="Enter city name…"
      />
    </div>

    <ul v-if="geocodeResults.length" class="weather-geocode-results">
      <li
        v-for="result in geocodeResults"
        :key="result.id"
        class="weather-geocode-result"
        @click="pickGeoResult(result)"
      >
        <span class="weather-geocode-result__name">{{ result.name }}</span>
        <span class="weather-geocode-result__meta">
          {{ [result.admin1, result.country].filter(Boolean).join(', ') }}
        </span>
      </li>
    </ul>

    <div v-if="geocodeError" class="alert alert-warning mt-2 mb-0">{{ geocodeError }}</div>
    <div v-if="error" class="alert alert-danger mt-2 mb-0">{{ error }}</div>

    <div v-if="loading && !weatherData" class="weather-loading" aria-live="polite">
      <span class="weather-loading-dot" aria-hidden="true"></span>
      <span class="weather-loading-dot" aria-hidden="true"></span>
      <span class="weather-loading-dot" aria-hidden="true"></span>
      <span class="visually-hidden">Loading weather data…</span>
    </div>

    <template v-if="weatherData">

      <div class="weather-bento-heading weather-bento-heading--chart">
        Weather chart
        <div class="weather-chart-controls">
          <div class="weather-chart-pan" role="group" aria-label="Pan chart">
            <button type="button" class="weather-chart-pan-btn" @click="panChart(-(chart24h ? 1 : 6))" title="Earlier">
              <i class="fa fa-chevron-left" aria-hidden="true"></i>
            </button>
            <button type="button" class="weather-chart-pan-btn" @click="panChart(chart24h ? 1 : 6)" title="Later">
              <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
          <div class="weather-chart-zoom-toggle" role="group" aria-label="Chart time range">
            <button
              type="button"
              :class="['weather-chart-zoom-btn', { 'weather-chart-zoom-btn--active': !chart24h }]"
              @click="set24h(false)"
            >72h</button>
            <button
              type="button"
              :class="['weather-chart-zoom-btn', { 'weather-chart-zoom-btn--active': chart24h }]"
              @click="set24h(true)"
            >24h</button>
          </div>
        </div>
      </div>
      <div class="weather-chart-wrap rounded-3 shadow-sm">
        <div v-if="loading" class="weather-loading" aria-live="polite">
          <span class="weather-loading-dot" aria-hidden="true"></span>
          <span class="weather-loading-dot" aria-hidden="true"></span>
          <span class="weather-loading-dot" aria-hidden="true"></span>
          <span class="visually-hidden">Loading weather data…</span>
        </div>
        <div ref="chartMain" class="weather-hc-container"></div>
      </div>

      <div class="weather-bento-section">
        <div class="weather-bento-heading">Now</div>
        <div class="weather-bento weather-bento--now">

          <!-- Hero: current temperature — spans 2 rows -->
          <div class="weather-stat-card weather-stat-card--area-temp weather-stat-card--hero rounded-3 shadow-sm">
            <div class="weather-hero-icon-box" aria-hidden="true">
              <img
                v-if="currentWeatherIconUrl"
                :src="currentWeatherIconUrl"
                class="weather-hero-icon"
                alt=""
              >
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value">{{ currentTemperature }}</span>
                <span class="weather-stat-unit text-muted">°C</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Now</span>
            </div>
          </div>

          <!-- Conditions — wide -->
          <div class="weather-stat-card weather-stat-card--area-cond rounded-3 shadow-sm" :title="currentConditions">
            <div class="weather-stat-icon weather-stat-icon--conditions">
              <i class="fa fa-cloud" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value weather-stat-value--sm">{{ currentConditions }}</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Conditions</span>
            </div>
          </div>

          <!-- Feels like -->
          <div class="weather-stat-card weather-stat-card--area-feel rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--feels">
              <i class="fa fa-thermometer-quarter" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value">{{ currentApparent }}</span>
                <span class="weather-stat-unit text-muted">°C</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Feels like</span>
            </div>
          </div>

          <!-- Wind -->
          <div class="weather-stat-card weather-stat-card--area-wind rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--wind">
              <i class="fa fa-flag" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value">{{ currentWind }}</span>
                <span class="weather-stat-unit text-muted">m/s</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Wind</span>
            </div>
          </div>

          <!-- Pressure -->
          <div class="weather-stat-card weather-stat-card--area-pres rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--pressure">
              <i class="fa fa-tachometer" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value">{{ currentPressure }}</span>
                <span class="weather-stat-unit text-muted">hPa</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Pressure</span>
            </div>
          </div>

        </div>
      </div>

      <div class="weather-bento-section">
        <div class="weather-bento-heading">Today</div>
        <div class="weather-bento weather-bento--today">

          <!-- Conditions -->
          <div class="weather-stat-card weather-stat-card--area-cond rounded-3 shadow-sm" :title="todayConditions">
            <div class="weather-stat-icon weather-stat-icon--conditions">
              <i class="fa fa-cloud" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value weather-stat-value--sm">{{ todayConditions }}</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Conditions</span>
            </div>
          </div>

          <!-- Today Hi / Lo -->
          <div class="weather-stat-card weather-stat-card--area-hilo rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--minmax">
              <i class="fa fa-arrows-v" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-hi"><i class="fa fa-long-arrow-up" aria-hidden="true"></i> {{ todayMaxTemp }}°</span>
                <span class="weather-stat-divider text-muted">/</span>
                <span class="weather-stat-lo"><i class="fa fa-long-arrow-down" aria-hidden="true"></i> {{ todayMinTemp }}°</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Hi / lo</span>
            </div>
          </div>

          <!-- UV index -->
          <div class="weather-stat-card weather-stat-card--area-uv rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--uv">
              <i class="fa fa-sun-o" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value">{{ todayMaxUv }}</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">UV index</span>
            </div>
          </div>

          <!-- Sunrise + Sunset combined -->
          <div class="weather-stat-card weather-stat-card--area-rsst rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--sunrise">
              <i class="fa fa-clock-o" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline gap-1">
                <span class="weather-stat-value weather-stat-value--paired">{{ todaySunrise }}</span>
                <span class="text-muted">/</span>
                <span class="weather-stat-value weather-stat-value--paired">{{ todaySunset }}</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Rise / Set</span>
            </div>
          </div>

          <!-- Moon phase -->
          <div class="weather-stat-card weather-stat-card--area-moon rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--moon">
              <img :src="'/images/weather-icons/' + moonPhase.icon" alt="" aria-hidden="true" style="width:1.4rem;height:1.4rem;object-fit:contain;">
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value weather-stat-value--sm">{{ moonPhase.name }}</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Moon phase</span>
            </div>
          </div>

        </div>
      </div>

      <div class="weather-bento-section">
        <div class="weather-bento-heading">Tomorrow</div>
        <div class="weather-bento weather-bento--today">

          <!-- Conditions -->
          <div class="weather-stat-card weather-stat-card--area-cond rounded-3 shadow-sm" :title="tomorrowConditions">
            <div class="weather-stat-icon weather-stat-icon--conditions">
              <i class="fa fa-cloud" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value weather-stat-value--sm">{{ tomorrowConditions }}</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Conditions</span>
            </div>
          </div>

          <!-- Tomorrow Hi / Lo -->
          <div class="weather-stat-card weather-stat-card--area-hilo rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--minmax">
              <i class="fa fa-arrows-v" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-hi"><i class="fa fa-long-arrow-up" aria-hidden="true"></i> {{ tomorrowMaxTemp }}°</span>
                <span class="weather-stat-divider text-muted">/</span>
                <span class="weather-stat-lo"><i class="fa fa-long-arrow-down" aria-hidden="true"></i> {{ tomorrowMinTemp }}°</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Hi / lo</span>
            </div>
          </div>

          <!-- UV index -->
          <div class="weather-stat-card weather-stat-card--area-uv rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--uv">
              <i class="fa fa-sun-o" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value">{{ tomorrowMaxUv }}</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">UV index</span>
            </div>
          </div>

          <!-- Sunrise + Sunset combined -->
          <div class="weather-stat-card weather-stat-card--area-rsst rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--sunrise">
              <i class="fa fa-clock-o" aria-hidden="true"></i>
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline gap-1">
                <span class="weather-stat-value weather-stat-value--paired">{{ tomorrowSunrise }}</span>
                <span class="text-muted">/</span>
                <span class="weather-stat-value weather-stat-value--paired">{{ tomorrowSunset }}</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Rise / Set</span>
            </div>
          </div>

          <!-- Moon phase -->
          <div class="weather-stat-card weather-stat-card--area-moon rounded-3 shadow-sm">
            <div class="weather-stat-icon weather-stat-icon--moon">
              <img :src="'/images/weather-icons/' + tomorrowMoonPhase.icon" alt="" aria-hidden="true" style="width:1.4rem;height:1.4rem;object-fit:contain;">
            </div>
            <div class="weather-stat-body d-flex flex-column">
              <div class="weather-stat-main d-flex align-items-baseline">
                <span class="weather-stat-value weather-stat-value--sm">{{ tomorrowMoonPhase.name }}</span>
              </div>
              <span class="weather-stat-label text-muted text-uppercase">Moon phase</span>
            </div>
          </div>

        </div>
      </div>

    </template>

    <div class="weather-links">
      <a
        :href="activeYrnoUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="weather-link"
      ><i class="fa fa-external-link" aria-hidden="true"></i> yr.no</a>
      <a
        :href="activeIlmateenistusUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="weather-link"
      ><i class="fa fa-external-link" aria-hidden="true"></i> ilmateenistus.ee</a>
      <span v-if="fetchedAt" class="weather-updated">Updated {{ fetchedAt }}</span>
      <button
        type="button"
        class="weather-link weather-link--refresh"
        :disabled="loading"
        @click="fetchWeather(true)"
        title="Refresh weather data"
      ><i class="fa fa-refresh" :class="{ 'fa-spin': loading }" aria-hidden="true"></i> Refresh</button>
    </div>
  </div>
</template>

<script>
import Highcharts from 'highcharts';

// Suppress Highcharts non-commercial warning for personal use
Highcharts.setOptions({
  lang: {
    thousandsSep: ',',
  },
  credits: { enabled: false },
});

const ICON_BASE = '/images/weather-icons/';

function weatherCodeToIcon(code, isDay = 1) {
  const d = isDay ? 'day' : 'night';
  if (code === 0 || code === 1)  return `clear-${d}.svg`;
  if (code === 2)                return `partly-cloudy-${d}.svg`;
  if (code === 3)                return 'overcast.svg';
  if (code >= 45 && code <= 48)  return `fog-${d}.svg`;
  if (code >= 51 && code <= 55)  return 'drizzle.svg';
  if (code >= 61 && code <= 65)  return 'rain.svg';
  if (code >= 71 && code <= 77)  return 'snow.svg';
  if (code >= 80 && code <= 82)  return `partly-cloudy-${d}-rain.svg`;
  if (code >= 85 && code <= 86)  return `partly-cloudy-${d}-snow.svg`;
  if (code === 95)               return `thunderstorms-${d}.svg`;
  if (code === 96 || code === 99) return `thunderstorms-${d}-rain.svg`;
  return 'not-available.svg';
}

const SEVERITY_TIER = {
  0: 0, 1: 0,           // clear
  2: 1, 3: 1,           // cloudy
  45: 2, 48: 2,         // fog
  51: 3, 53: 3, 55: 3,  // drizzle
  61: 4, 63: 4, 65: 4,  // rain
  71: 4, 73: 4, 75: 4, 77: 4, // snow
  80: 5, 81: 5, 82: 5,  // showers
  85: 5, 86: 5,         // snow showers
  95: 6, 96: 6, 99: 6,  // thunderstorm
};

const WEATHER_CODES = {
  0: 'Clear sky',
  1: 'Mainly clear',
  2: 'Partly cloudy',
  3: 'Overcast',
  45: 'Foggy',
  48: 'Depositing rime fog',
  51: 'Light drizzle',
  53: 'Moderate drizzle',
  55: 'Dense drizzle',
  61: 'Slight rain',
  63: 'Moderate rain',
  65: 'Heavy rain',
  71: 'Slight snow',
  73: 'Moderate snow',
  75: 'Heavy snow',
  77: 'Snow grains',
  80: 'Slight showers',
  81: 'Moderate showers',
  82: 'Violent showers',
  85: 'Slight snow showers',
  86: 'Heavy snow showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with hail',
  99: 'Thunderstorm with heavy hail',
};

export default {
  name: 'WeatherWidget',

  data() {
    return {
      presetCities: [
        { key: 'paide',   label: 'Paide',   lat: 58.8853, lon: 25.5569 },
        { key: 'parnu',   label: 'Pärnu',   lat: 58.3790, lon: 24.5278 },
        { key: 'kiili',   label: 'Kiili',   lat: 59.3069, lon: 24.8378 },
        { key: 'turi',    label: 'Türi',    lat: 58.8055, lon: 25.4339 },
        { key: 'tallinn', label: 'Tallinn', lat: 59.437,  lon: 24.7535 },
      ],

      selectedCity: 'paide',
      customCityName: '',

      activeLat: 59.437,
      activeLon: 24.7535,

      geocodeResults: [],

      weatherData: null,
      loading: false,
      error: null,
      geocodeError: null,

      chartMain: null,
      chart24h: false,
    };
  },

  computed: {
    activeYrnoUrl() {
      return `https://www.yr.no/en/forecast/graph/${this.activeLat},${this.activeLon}`;
    },

    activeIlmateenistusUrl() {
      return `https://www.ilmateenistus.ee/ilm/prognoosid/asukoha-prognoos/?coordinates=${this.activeLat};${this.activeLon}`;
    },

    hourly() {
      return this.weatherData?.hourly ?? null;
    },

    currentIndex() {
      if (!this.hourly) return 0;
      const now = new Date();
      let idx = 0;
      for (let i = 0; i < this.hourly.time.length; i++) {
        if (this.parseApiTime(this.hourly.time[i]) <= now) {
          idx = i;
        } else {
          break;
        }
      }
      return idx;
    },

    currentTemperature() {
      if (!this.hourly) return '–';
      const val = this.hourly.temperature_2m[this.currentIndex];
      return val !== null ? Math.round(val) : '–';
    },

    currentApparent() {
      if (!this.hourly) return '–';
      const val = this.hourly.apparent_temperature[this.currentIndex];
      return val !== null ? Math.round(val) : '–';
    },

    todayMinTemp() {
      if (!this.hourly) return '–';
      const today = this.localDateStr();
      const vals = this.hourly.time
        .map((t, i) => (t.startsWith(today) ? this.hourly.temperature_2m[i] : null))
        .filter(v => v !== null);
      return vals.length ? Math.round(Math.min(...vals)) : '–';
    },

    todayMaxTemp() {
      if (!this.hourly) return '–';
      const today = this.localDateStr();
      const vals = this.hourly.time
        .map((t, i) => (t.startsWith(today) ? this.hourly.temperature_2m[i] : null))
        .filter(v => v !== null);
      return vals.length ? Math.round(Math.max(...vals)) : '–';
    },

    todayMaxUv() {
      if (!this.hourly) return '–';
      const today = this.localDateStr();
      const values = this.hourly.time
        .map((t, i) => (t.startsWith(today) ? this.hourly.uv_index[i] : null))
        .filter(v => v !== null);
      return values.length ? Math.round(Math.max(...values) * 10) / 10 : '–';
    },

    currentPressure() {
      if (!this.hourly) return '–';
      const val = this.hourly.surface_pressure[this.currentIndex];
      return val !== null ? Math.round(val) : '–';
    },

    currentConditions() {
      if (!this.hourly) return '–';
      const code = this.hourly.weathercode[this.currentIndex];
      return WEATHER_CODES[code] ?? `Code ${code}`;
    },

    currentWeatherIconUrl() {
      if (!this.hourly) return null;
      const code  = this.hourly.weathercode[this.currentIndex];
      const isDay = this.hourly.is_day[this.currentIndex];
      return ICON_BASE + weatherCodeToIcon(code, isDay);
    },

    currentWind() {
      if (!this.hourly) return '–';
      return (this.hourly.windspeed_10m[this.currentIndex] / 3.6).toFixed(1);
    },

    todaySunrise() {
      const times = this.weatherData?.daily?.sunrise;
      if (!times?.length) return '–';
      return times[0].split('T')[1]?.slice(0, 5) ?? '–';
    },

    todaySunset() {
      const times = this.weatherData?.daily?.sunset;
      if (!times?.length) return '–';
      return times[0].split('T')[1]?.slice(0, 5) ?? '–';
    },

    tomorrowMinTemp() {
      if (!this.hourly) return '–';
      const tomorrow = this.localDateStr(1);
      const vals = this.hourly.time
        .map((t, i) => (t.startsWith(tomorrow) ? this.hourly.temperature_2m[i] : null))
        .filter(v => v !== null);
      return vals.length ? Math.round(Math.min(...vals)) : '–';
    },

    tomorrowMaxTemp() {
      if (!this.hourly) return '–';
      const tomorrow = this.localDateStr(1);
      const vals = this.hourly.time
        .map((t, i) => (t.startsWith(tomorrow) ? this.hourly.temperature_2m[i] : null))
        .filter(v => v !== null);
      return vals.length ? Math.round(Math.max(...vals)) : '–';
    },

    tomorrowMaxUv() {
      if (!this.hourly) return '–';
      const tomorrow = this.localDateStr(1);
      const values = this.hourly.time
        .map((t, i) => (t.startsWith(tomorrow) ? this.hourly.uv_index[i] : null))
        .filter(v => v !== null);
      return values.length ? Math.round(Math.max(...values) * 10) / 10 : '–';
    },

    tomorrowSunrise() {
      const times = this.weatherData?.daily?.sunrise;
      if (!times?.[1]) return '–';
      return times[1].split('T')[1]?.slice(0, 5) ?? '–';
    },

    tomorrowSunset() {
      const times = this.weatherData?.daily?.sunset;
      if (!times?.[1]) return '–';
      return times[1].split('T')[1]?.slice(0, 5) ?? '–';
    },

    todayConditions() {
      if (!this.hourly) return '–';
      const today = this.localDateStr();
      const parts = [this.getDayConditions(today), ...this.getDayHighlights(today)];
      return parts.join(' · ');
    },

    tomorrowConditions() {
      if (!this.hourly) return '–';
      const tomorrow = this.localDateStr(1);
      const parts = [this.getDayConditions(tomorrow), ...this.getDayHighlights(tomorrow)];
      return parts.join(' · ');
    },

    moonPhase() {
      return this.calcMoonPhase(Date.now());
    },

    tomorrowMoonPhase() {
      return this.calcMoonPhase(Date.now() + 86400000);
    },

    fetchedAt() {
      const t = this.weatherData?.fetched_at;
      if (!t) return null;
      // API returns HH:MM:SS — show only HH:MM
      return t.slice(0, 5);
    },
  },

  methods: {
    selectPresetCity(city) {
      this.selectedCity = city.key;
      this.activeLat = city.lat;
      this.activeLon = city.lon;
      this.error = null;
      this.geocodeError = null;
      this.geocodeResults = [];
      localStorage.setItem('weatherCity', JSON.stringify({ key: city.key, lat: city.lat, lon: city.lon }));
      this.fetchWeather();
    },

    selectCustom() {
      this.selectedCity = 'custom';
      this.error = null;
      this.geocodeError = null;
      this.geocodeResults = [];
    },

    async searchCustomCity() {
      const name = this.customCityName.trim();
      if (name.length < 2) return;

      this.geocodeError = null;
      this.error = null;

      try {
        const res = await axios.get('/api/weather/geocode', { params: { name } });
        const results = res.data?.results;
        if (!results || results.length === 0) {
          this.geocodeError = `No results found for "${name}".`;
          return;
        }
        this.geocodeResults = results;
      } catch {
        this.geocodeError = 'City lookup failed. Please try again.';
      }
    },

    async pickGeoResult(result) {
      this.geocodeResults = [];
      this.activeLat = result.latitude;
      this.activeLon = result.longitude;
      localStorage.setItem('weatherCity', JSON.stringify({ key: 'custom', lat: result.latitude, lon: result.longitude, name: this.customCityName }));
      await this.fetchWeather();
    },

    async fetchWeather(force = false) {
      this.loading = true;
      this.error = null;
      this.destroyCharts();

      try {
        const params = { lat: this.activeLat, lon: this.activeLon };
        if (force) params.force = 1;
        const res = await axios.get('/api/weather', { params });
        this.weatherData = res.data;
      } catch (e) {
        this.error = e.response?.data?.error ?? 'Failed to load weather data.';
        this.weatherData = null;
      } finally {
        this.loading = false;
      }

      if (this.weatherData) {
        await this.$nextTick();
        this.buildCharts();
      }
    },

    destroyCharts() {
      if (this.chartMain) {
        this.chartMain.destroy();
        this.chartMain = null;
      }
    },

    buildCharts() {
      if (!this.hourly) return;

      const categories = this.hourly.time.map(t => {
        const [datePart, timePart] = t.split('T');
        const [y, m, d] = datePart.split('-').map(Number);
        const weekday = new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short' });
        return `${weekday} ${timePart}`;
      });

      const times  = this.hourly.time;
      const codes  = this.hourly.weathercode;
      const isDays = this.hourly.is_day;
      this.buildMainChart(categories, times, codes, isDays);
      this.applyChartZoom();
    },

    set24h(enabled) {
      this.chart24h = enabled;
      this.applyChartZoom();
    },

    applyChartZoom() {
      if (!this.chartMain) return;
      const total = this.hourly.time.length - 1;
      const start = this.currentIndex;
      if (this.chart24h) {
        const end = Math.min(start + 24, total);
        this.chartMain.xAxis[0].update({ tickInterval: 2 }, false);
        this.chartMain.xAxis[0].setExtremes(start, end);
      } else {
        const end = Math.min(start + 72, total);
        this.chartMain.xAxis[0].update({ tickInterval: 6 }, false);
        this.chartMain.xAxis[0].setExtremes(start, end);
      }
    },

    panChart(hours) {
      if (!this.chartMain) return;
      const ax      = this.chartMain.xAxis[0];
      const total   = this.hourly.time.length - 1;
      const span    = Math.round(ax.max - ax.min);
      const newMin  = Math.max(0, Math.min(ax.min + hours, total - span));
      const newMax  = newMin + span;
      ax.setExtremes(newMin, newMax);
    },

    drawDualColorLegendLine(chart, seriesIndex, storeKey, color, lineWidth, dashStyle) {
      if (chart[storeKey]) {
        chart[storeKey].destroy();
        chart[storeKey] = null;
      }

      const series     = chart.series[seriesIndex];
      const legendItem = series?.legendItem;
      if (!legendItem?.group) return;

      const symbolWidth = chart.legend.symbolWidth ?? 16;

      const lineEl = legendItem.line ?? legendItem.symbol;
      let lineY = 0;
      if (lineEl?.element) {
        const b = lineEl.element.getBBox();
        lineY = b.y + b.height / 2;
      }

      const attrs = { stroke: color, 'stroke-width': lineWidth, zIndex: 7 };
      if (dashStyle) attrs['stroke-dasharray'] = dashStyle;

      chart[storeKey] = chart.renderer
        .path(['M', 0, lineY + 5, 'L', symbolWidth, lineY + 5])
        .attr(attrs)
        .add(legendItem.group);
    },

    drawTempLegendSymbol(chart) {
      this.drawDualColorLegendLine(chart, 0, '_tempLegendLine',       'rgb(70,130,220)',         2,   null);
      this.drawDualColorLegendLine(chart, 1, '_feelsLikeLegendLine',  'rgba(70,130,220,0.35)',   1.5, '4,2');
    },

    visibleRange(chart, dataLength) {
      const xAxis  = chart.xAxis[0];
      const visMin = Math.round(xAxis.min ?? 0);
      const visMax = Math.round(xAxis.max ?? dataLength - 1);
      return { visMin, visMax, visCount: visMax - visMin + 1 };
    },

    drawWindSpeeds(chart) {
      if (chart._wxWind) {
        chart._wxWind.forEach(el => el.destroy());
      }
      chart._wxWind = [];

      const xAxis  = chart.xAxis[0];
      const speeds = this.hourly.windspeed_10m;
      const { visMin, visMax, visCount } = this.visibleRange(chart, speeds.length);

      const step = visCount <= 30
        ? (chart.plotWidth < 400 ? 4  : chart.plotWidth < 600 ? 3 : 2)
        : visCount <= 80
          ? (chart.plotWidth < 400 ? 12 : chart.plotWidth < 600 ? 8 : 6)
          : (chart.plotWidth < 320 ? 12 : chart.plotWidth < 500 ? 6 : chart.plotWidth < 700 ? 4 : 2);

      const y = chart.plotTop + chart.plotHeight + 22;

      chart._wxWind.push(
        chart.renderer
          .text('m/s', chart.plotLeft - 4, y)
          .attr({ align: 'right', zIndex: 5 })
          .css({ fontSize: '9px', color: '#bbb', cursor: 'default' })
          .add()
      );

      const windIndices = [];
      for (let i = visMin; i <= visMax; i += step) windIndices.push(i);
      if ((visMax - visMin) % step > step / 2) windIndices.push(visMax);

      for (const i of windIndices) {
        const x = xAxis.toPixels(i, false);
        if (x < chart.plotLeft - 2 || x > chart.plotLeft + chart.plotWidth + 8) continue;
        chart._wxWind.push(
          chart.renderer
            .text((speeds[i] / 3.6).toFixed(1), x, y)
            .attr({ align: 'center', zIndex: 5 })
            .css({ fontSize: '10px', color: '#888', cursor: 'default' })
            .add()
        );
      }
    },

    drawWeatherIcons(chart) {
      if (chart._wxIcons) {
        chart._wxIcons.forEach(el => el.destroy());
      }
      chart._wxIcons = [];

      const xAxis   = chart.xAxis[0];
      const plotTop = chart.plotTop;
      const codes   = this.hourly.weathercode;
      const isDays  = this.hourly.is_day;
      const { visMin, visMax, visCount } = this.visibleRange(chart, codes.length);

      const step = visCount <= 30
        ? (chart.plotWidth < 400 ? 4  : chart.plotWidth < 600 ? 3 : 2)
        : visCount <= 80
          ? (chart.plotWidth < 400 ? 12 : chart.plotWidth < 600 ? 8 : 6)
          : (chart.plotWidth < 320 ? 12 : chart.plotWidth < 500 ? 6 : chart.plotWidth < 700 ? 6 : 4);

      const size = 24;
      const y    = plotTop - size - 6;

      const indices = [];
      for (let i = visMin; i <= visMax; i += step) indices.push(i);
      if ((visMax - visMin) % step > step / 2) indices.push(visMax);

      for (const i of indices) {
        const x = xAxis.toPixels(i, false);
        if (x < chart.plotLeft - 2 || x > chart.plotLeft + chart.plotWidth + size / 2) continue;
        const src = ICON_BASE + weatherCodeToIcon(codes[i], isDays[i]);
        chart._wxIcons.push(
          chart.renderer
            .image(src, x - size / 2, y, size, size)
            .attr({ zIndex: 6 })
            .add()
        );
      }
    },

    buildMainChart(categories, times, codes, isDays) {
      const self      = this;
      const clouds    = this.hourly.cloudcover;
      const winds     = this.hourly.windspeed_10m;
      const uvs       = this.hourly.uv_index;
      const pressures = this.hourly.surface_pressure;
      const apparents = this.hourly.apparent_temperature;


      this.chartMain = Highcharts.chart(this.$refs.chartMain, {
        chart: {
          height: 310,
          style: { fontFamily: 'inherit' },
          marginTop: 54,
          marginBottom: 90,
          events: {
            render() {
              self.drawWeatherIcons(this);
              self.drawWindSpeeds(this);
              self.drawTempLegendSymbol(this);
            },
          },
          animation: false,
        },
        title: { text: null },
        credits: { enabled: false },
        legend: {
          verticalAlign: 'bottom',
          align: 'center',
          margin: 12,
          itemStyle: { fontWeight: '500', fontSize: '12px' },
          symbolRadius: 2,
        },
        tooltip: {
          shared: true,
          useHTML: true,
          backgroundColor: 'rgba(255,255,255,0.97)',
          borderColor: 'rgba(0,0,0,0.1)',
          borderRadius: 8,
          shadow: { opacity: 0.08 },
          formatter() {
            const idx       = this.points[0].point.index;
            const time      = times[idx].replace('T', ' ');
            const icon      = ICON_BASE + weatherCodeToIcon(codes[idx], isDays[idx]);
            const condition = WEATHER_CODES[codes[idx]] ?? `Code ${codes[idx]}`;
            let s = `<span style="font-size:11px;font-weight:600;color:#555">${time}</span><br/>`;
            s += `<img src="${icon}" style="width:16px;height:16px;vertical-align:middle;margin-right:4px"><span style="font-size:12px;color:#444;font-weight:500">${condition}</span><br/>`;
            this.points.forEach(pt => {
              const suffix = pt.series.options.custom?.suffix ?? '';
              const dp     = pt.series.options.custom?.dp ?? 1;
              s += `<span style="color:${pt.color}">●</span> ${pt.series.name}: <b>${Highcharts.numberFormat(pt.y, dp)}</b>${suffix}<br/>`;
            });
            s += `<span style="color:#aaa">●</span> Cloud cover: <b>${clouds[idx]}</b>%<br/>`;
            s += `<span style="color:#aaa">●</span> Wind speed: <b>${(winds[idx] / 3.6).toFixed(1)}</b> m/s<br/>`;
            s += `<span style="color:#aaa">●</span> UV index: <b>${uvs[idx].toFixed(1)}</b><br/>`;
            s += `<span style="color:#aaa">●</span> Pressure: <b>${Math.round(pressures[idx])}</b> hPa<br/>`;
            return s;
          },
        },
        xAxis: {
          categories,
          plotBands: [],
          plotLines: [{
            value: this.currentIndex,
            color: 'rgba(180, 80, 40, 0.45)',
            width: 1.5,
            dashStyle: 'ShortDash',
            zIndex: 5,
          }],
          tickInterval: 6,
          labels: {
            step: 1,
            y: 38,
            style: { fontSize: '11px' },
            formatter() {
              const [day, time] = this.value.split(' ');
              const hour = time.split(':')[0];
              return hour === '00' ? `${day} ${hour}` : hour;
            },
          },
          crosshair: { color: 'rgba(0,0,0,0.08)', width: 1 },
          lineColor: 'rgba(0,0,0,0.1)',
          tickColor: 'rgba(0,0,0,0.1)',
        },
        responsive: {
          rules: [
            {
              condition: { maxWidth: 500 },
              chartOptions: {
                chart: { marginBottom: 115 },
                xAxis: { tickInterval: 12 },
              },
            },
            {
              condition: { maxWidth: 320 },
              chartOptions: {
                chart: { marginBottom: 115 },
                xAxis: { tickInterval: 24 },
              },
            },
          ],
        },
        yAxis: [
          {
            title: { text: '°C', style: { fontSize: '11px' } },
            labels: { style: { fontSize: '11px' } },
            gridLineColor: 'rgba(0,0,0,0.06)',
          },
          {
            title: { text: 'mm', style: { fontSize: '11px' } },
            labels: { style: { fontSize: '11px' } },
            min: 0,
            opposite: true,
            gridLineWidth: 0,
          },
        ],
        series: [
          {
            name: 'Temperature',
            type: 'areaspline',
            data: this.hourly.temperature_2m,
            yAxis: 0,
            color: 'rgb(220, 70, 50)',
            fillColor: 'rgba(0,0,0,0)',
            zones: [
              { value: 0, color: 'rgb(70,130,220)' },
              { color: 'rgb(220,70,50)' },
            ],
            lineWidth: 2.5,
            marker: { enabled: false, states: { hover: { enabled: true, radius: 4 } } },
            zIndex: 2,
            custom: { suffix: '°C', dp: 1 },
          },
          {
            name: 'Feels like',
            type: 'spline',
            data: this.hourly.apparent_temperature,
            yAxis: 0,
            color: 'rgba(220,70,50,0.35)',
            zones: [
              { value: 0, color: 'rgba(70,130,220,0.35)' },
              { color: 'rgba(220,70,50,0.35)' },
            ],
            lineWidth: 1.5,
            dashStyle: 'ShortDash',
            marker: { enabled: false, states: { hover: { enabled: true, radius: 4 } } },
            zIndex: 1,
            custom: { suffix: '°C', dp: 1 },
          },
          {
            name: 'Rain',
            type: 'column',
            data: this.hourly.rain,
            yAxis: 1,
            color: 'rgba(54,162,235,0.55)',
            borderColor: 'rgba(54,162,235,0.8)',
            borderRadius: 2,
            borderWidth: 1,
            stacking: 'normal',
            zIndex: 1,
            custom: { suffix: ' mm', dp: 2 },
          },
          {
            name: 'Snow',
            type: 'column',
            data: this.hourly.snowfall,
            yAxis: 1,
            color: 'rgba(180,210,240,0.75)',
            borderColor: 'rgba(160,195,230,0.9)',
            borderRadius: 2,
            borderWidth: 1,
            stacking: 'normal',
            zIndex: 1,
            custom: { suffix: ' mm', dp: 2 },
          },
        ],
        plotOptions: {
          series: { animation: false },
          areaspline: { threshold: null },
        },
      });
    },

    worstCodeInSlice(entries) {
      return entries.reduce((best, h) =>
        (SEVERITY_TIER[h.code] ?? 0) > (SEVERITY_TIER[best.code] ?? 0) ? h : best
      ).code;
    },

    getDayConditions(dateStr, fromIndex = 0) {
      const entries = this.hourly.time
        .map((t, i) => (t.startsWith(dateStr) && this.hourly.is_day[i] === 1 && i >= fromIndex)
          ? { code: this.hourly.weathercode[i] } : null)
        .filter(Boolean);
      if (!entries.length) return '–';

      const mid        = Math.floor(entries.length / 2);
      const earlyCode  = this.worstCodeInSlice(entries.slice(0, mid));
      const lateCode   = this.worstCodeInSlice(entries.slice(mid));
      const earlyTier  = SEVERITY_TIER[earlyCode] ?? 0;
      const lateTier   = SEVERITY_TIER[lateCode]  ?? 0;
      const earlyLabel = WEATHER_CODES[earlyCode] ?? `Code ${earlyCode}`;
      const lateLabel  = WEATHER_CODES[lateCode]  ?? `Code ${lateCode}`;

      if (earlyTier === lateTier) return earlyLabel;
      if (lateTier  > earlyTier) return `${earlyLabel}, ${lateLabel.toLowerCase()} later`;
      return `${earlyLabel}, clearing to ${lateLabel.toLowerCase()}`;
    },

    getSeasonalThresholds() {
      const m = new Date().getMonth(); // 0 = Jan
      if (m <= 1 || m === 11) return {             // Dec–Feb (winter)
        high:    { min: 5,   label: 'Unusually warm' },
        midHigh: null,
        veryLow: { max: -15, label: 'Extreme cold'   },
        low:     { max: -5,  label: 'Very cold'      },
      };
      if (m <= 4) return {                         // Mar–May (spring)
        high:    { min: 20,  label: 'Warm'           },
        midHigh: null,
        veryLow: { max: -8,  label: 'Very cold'      },
        low:     { max: 0,   label: 'Freezing'       },
      };
      if (m <= 7) return {                         // Jun–Aug (summer)
        high:    { min: 30,  label: 'Very hot'       },
        midHigh: { min: 25,  label: 'Hot'            },
        veryLow: null,
        low:     { max: 12,  label: 'Unusually cool' },
      };
      return {                                     // Sep–Nov (autumn)
        high:    { min: 18,  label: 'Warm'           },
        midHigh: null,
        veryLow: { max: -5,  label: 'Very cold'      },
        low:     { max: 2,   label: 'Cold'           },
      };
    },

    getDayHighlights(dateStr) {
      const highlights = [];
      const indices = this.hourly.time
        .map((t, i) => t.startsWith(dateStr) ? i : null)
        .filter(v => v !== null);
      if (!indices.length) return highlights;

      const hour   = i => parseInt(this.hourly.time[i].split('T')[1]);
      const isNightHour = h => h < 8 || h >= 22;
      const dayIdx = indices.filter(i => !isNightHour(hour(i)));

      const temps  = i => this.hourly.temperature_2m[i];
      const maxTemp = dayIdx.length ? Math.max(...dayIdx.map(temps)) : -Infinity;

      const minIdx     = indices.reduce((best, i) => temps(i) < temps(best) ? i : best, indices[0]);
      const minTemp    = temps(minIdx);
      const minIsNight = isNightHour(hour(minIdx));

      const maxWind = Math.max(...indices.map(i => this.hourly.windspeed_10m[i])); // km/h
      const maxUv   = dayIdx.length ? Math.max(...dayIdx.map(i => this.hourly.uv_index[i])) : 0;

      const t = this.getSeasonalThresholds();

      // High temperature (daytime only) — check highest tier first
      if (t.high && maxTemp >= t.high.min)
        highlights.push(`${t.high.label} (${Math.round(maxTemp)}°C)`);
      else if (t.midHigh && maxTemp >= t.midHigh.min)
        highlights.push(`${t.midHigh.label} (${Math.round(maxTemp)}°C)`);

      // Low temperature — append "overnight" if the cold only occurs at night
      const overnight = minIsNight ? ' overnight' : '';
      if (t.veryLow && minTemp <= t.veryLow.max)
        highlights.push(`${t.veryLow.label}${overnight} (${Math.round(minTemp)}°C)`);
      else if (t.low && minTemp <= t.low.max)
        highlights.push(`${t.low.label}${overnight} (${Math.round(minTemp)}°C)`);

      if (maxWind >= 61)       highlights.push('Gale-force winds'); // > 17 m/s
      else if (maxWind >= 40)  highlights.push('Strong winds');     // > 11 m/s

      if (maxUv >= 8)          highlights.push('Very high UV');
      else if (maxUv >= 6)     highlights.push('High UV');

      return highlights;
    },

    calcMoonPhase(timestamp) {
      const knownNew = Date.UTC(2000, 0, 6, 18, 14);
      const elapsed  = (timestamp - knownNew) / 86400000;
      const fraction = ((elapsed % 29.53059) + 29.53059) % 29.53059 / 29.53059;

      if (fraction < 0.0625 || fraction >= 0.9375) return { name: 'New moon',        icon: 'moon-new.svg' };
      if (fraction < 0.1875)                        return { name: 'Waxing crescent', icon: 'moon-waxing-crescent.svg' };
      if (fraction < 0.3125)                        return { name: 'First quarter',   icon: 'moon-first-quarter.svg' };
      if (fraction < 0.4375)                        return { name: 'Waxing gibbous',  icon: 'moon-waxing-gibbous.svg' };
      if (fraction < 0.5625)                        return { name: 'Full moon',       icon: 'moon-full.svg' };
      if (fraction < 0.6875)                        return { name: 'Waning gibbous',  icon: 'moon-waning-gibbous.svg' };
      if (fraction < 0.8125)                        return { name: 'Last quarter',    icon: 'moon-last-quarter.svg' };
      return                                               { name: 'Waning crescent', icon: 'moon-waning-crescent.svg' };
    },

    localDateStr(offsetDays = 0) {
      return new Date(Date.now() + offsetDays * 86400000).toLocaleDateString('sv');
    },

    parseApiTime(timeStr) {
      const [datePart, timePart] = timeStr.split('T');
      const [y, m, d] = datePart.split('-').map(Number);
      const [h, min] = timePart.split(':').map(Number);
      return new Date(y, m - 1, d, h, min);
    },
  },

  watch: {
    customCityName(val) {
      if (this._suppressSearch) return;
      clearTimeout(this._searchTimer);
      this.geocodeResults = [];
      this.geocodeError = null;
      const name = val.trim();
      if (name.length < 2) return;
      this._searchTimer = setTimeout(() => this.searchCustomCity(), 350);
    },
  },

  mounted() {
    const saved = JSON.parse(localStorage.getItem('weatherCity') || 'null');
    const city  = saved
      ? (this.presetCities.find(c => c.key === saved.key) ?? { key: 'custom', lat: saved.lat, lon: saved.lon })
      : this.presetCities.find(c => c.key === 'paide');
    if (city.key === 'custom') {
      this.selectedCity = 'custom';
      this.activeLat = city.lat;
      this.activeLon = city.lon;
      this._suppressSearch = true;
      this.customCityName = saved.name ?? '';
      this.$nextTick(() => { this._suppressSearch = false; });
      this.fetchWeather();
    } else {
      this.selectPresetCity(city);
    }

    this._refreshTimer = setInterval(() => this.fetchWeather(), 60 * 60 * 1000);
  },

  beforeDestroy() {
    clearInterval(this._refreshTimer);
    this.destroyCharts();
  },
};
</script>
